/**
 * Inky's tool registry (moved out of App.js). Each tool:
 *
 *   { name, description, parameters (JSON schema), risk: 'read'|'write'|'navigate',
 *     label(args, ctx) -> human step label, available?(ctx) -> bool,
 *     run(args, ctx) -> Promise<{ ok, summary, data, card? }>,
 *     approval?: 'confirm'|'form', commit?(values, ctx) -> Promise<{ ok, summary, outcome, entity? }> }
 *
 * `run` results: `data` goes back to the model (capped), `summary` is the one-line trace
 * text, `card` is a structured result card rendered in the chat. Write tools that touch
 * non-document entities have an `approval`: their `run` only PREPARES a card (nothing is
 * written); `commit` runs when the user confirms the card. Document edits apply as editor
 * suggestions (the editor's review UI is the approval), so doc.edit runs directly.
 *
 * The registry is built once per mount and reads the latest app state through a ref, so a
 * turn that outlives a re-render (or a panel remount) always sees fresh data.
 */

import { useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import edgeFunctionService from "../services/edgeFunctionService";
import { supabase } from "../lib/supabase";
import { hasReachedLimit } from "../services/usageService";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/EnterpriseAuthContext";
import { useAIRequests } from "../context/AIRequestContext";
import { useActiveEditor } from "../context/ActiveEditorContext";
import { useUsage } from "../context/UsageContext";
import { AI_TONES } from "../constants/aiTones";
import { oneLine } from "./inkyPersona";
import { htmlToText, snapshotEditor } from "./editorBridge";
import { loadSessionInsights, resolveActiveContext, sortRequirements } from "./projectGrounding";
import { runDocEdit } from "./docEditPlanner";
import { editorContextKey } from "./routeContext";
import { commitRequirementChanges, stageRequirementChanges } from "./requirementChanges";

/** How long app.navigate waits for an opened editor to register before the next model round. */
const EDITOR_READY_TIMEOUT_MS = 4000;
const EDITOR_READY_POLL_MS = 100;

/** Tone used when neither the user nor the model chose one. */
export const DEFAULT_TONE = "The Scholar";

/**
 * App path for an entity.
 * @param {'project'|'customer'|'session'|'document'|'template'} kind - Entity kind.
 * @param {object} entity - Entity with id (and templateType for templates).
 * @returns {string} In-app path.
 */
export const entityHref = (kind, entity) => {
  if (!entity?.id) return "/home";
  switch (kind) {
    case "project":
      return `/projects/${entity.id}`;
    case "customer":
      return `/customers/${entity.id}`;
    case "session":
      return `/customers/sessions/${entity.id}`;
    case "document":
      return `/documents/${entity.id}/edit`;
    case "template":
      return entity.templateType === "powerpoint"
        ? `/templates/powerpoint-rich/${entity.id}/edit`
        : `/templates/${entity.id}/edit`;
    default:
      return "/home";
  }
};

/**
 * Case-insensitive name match score (exact > prefix > contains > all words).
 * @param {string} name - Candidate name.
 * @param {string} query - Search text.
 * @returns {number} Score 0-100.
 */
const nameScore = (name, query) => {
  const a = String(name || "").toLowerCase().trim();
  const q = String(query || "").toLowerCase().trim();
  if (!a || !q) return 0;
  if (a === q) return 100;
  if (a.startsWith(q)) return 80;
  if (a.includes(q)) return 60;
  const words = q.split(/\s+/).filter((w) => w.length > 2);
  if (words.length && words.every((w) => a.includes(w))) return 40;
  return 0;
};

/**
 * Resolve an entity by id, else by best name match.
 * @param {Array<object>} items - Entities.
 * @param {{id?: string, name?: string}} ref - Id and/or name.
 * @returns {object|null} Entity.
 */
const resolveEntity = (items, { id, name } = {}) => {
  if (id) {
    const byId = (items || []).find((item) => item.id === id);
    if (byId) return byId;
  }
  const query = name || (id && !/^[0-9a-f-]{20,}$/i.test(id) ? id : "");
  if (!query) return null;
  let best = null;
  let bestScore = 0;
  for (const item of items || []) {
    const score = nameScore(item.name, query);
    if (score > bestScore) {
      best = item;
      bestScore = score;
    }
  }
  return bestScore >= 60 ? best : null;
};

/**
 * Text form of a session summary.
 * @param {*} summary - Raw summary.
 * @returns {string|null} Text.
 */
const summaryToText = (summary) => {
  if (!summary) return null;
  if (typeof summary === "string") return summary;
  if (typeof summary === "object") return summary.overview || summary.summary || summary.text || JSON.stringify(summary);
  return String(summary);
};

/**
 * Fire the shared "data changed" event list pages may listen to.
 * @param {string} kind - Entity kind.
 * @returns {void}
 */
const notifyDataChanged = (kind) => {
  try {
    window.dispatchEvent(new CustomEvent("quillai:data-changed", { detail: { kind, source: "inky" } }));
  } catch {
    // Non-browser environments: nothing listens.
  }
};

/** Tables whose rows reference a project; any row means a project Inky created is in use. */
const PROJECT_CHILD_TABLES = ["sessions", "requirements", "generated_documents", "project_context_files", "project_members"];

/**
 * Not-found result listing valid options so the model can correct itself.
 * @param {string} kind - Entity kind.
 * @param {string} wanted - What was asked for.
 * @param {Array<object>} items - Valid entities.
 * @returns {{ok: false, summary: string, data: object}} Tool result.
 */
const notFound = (kind, wanted, items) => ({
  ok: false,
  summary: `No ${kind} matched`,
  data: {
    error: `No ${kind} matches "${wanted || ""}".`,
    valid: (items || []).slice(0, 25).map((item) => ({ id: item.id, name: item.name })),
    hint: `Use one of these ids, or call data_search.`,
  },
});

/**
 * Build the registry. All state is read through `latest.current` at call time.
 * @param {{current: object}} latest - Ref with navigate, actions, contextData, route, etc.
 * @returns {Array<object>} Tools.
 */
const buildTools = (latest) => {
  const data = () => latest.current.contextData || {};
  const projects = () => data().projects || [];
  const customers = () => data().customers || [];
  const templates = () => data().templates || [];
  const active = () =>
    resolveActiveContext({ pathname: latest.current.pathname, search: latest.current.search, contextData: data() });
  const editorOpen = () => Boolean(latest.current.getEditorRef?.()?.current);
  const editorFallbackMeta = () => {
    const ctx = active();
    if (ctx.kind === "document") {
      return { kind: "document", documentId: ctx.document?.id || null, title: ctx.document?.name || "Document", readOnly: false };
    }
    if (ctx.kind === "template") {
      return { kind: "template", templateId: ctx.template?.id || null, title: ctx.template?.name || "Template", readOnly: false };
    }
    return { kind: "document", title: "Document", readOnly: false };
  };
  /**
   * Human name for an in-app path ("Alpha", "templates", "the document generator").
   * @param {string} path - In-app path.
   * @returns {string} Page name.
   */
  const describePath = (path) => {
    const target = String(path || "");
    const scope = resolveActiveContext({ pathname: target.split("?")[0], search: target.includes("?") ? target.slice(target.indexOf("?")) : "", contextData: data() });
    const named = scope.document?.name || scope.session?.name || scope.template?.name || scope.project?.name || scope.customer?.name;
    if (named) return `"${oneLine(named, 40)}"`;
    const page = target.split(/[/?]/).filter(Boolean)[0] || "home";
    return { generate: "the document generator", customers: target.includes("/sessions") ? "sessions" : "customers" }[page] || page;
  };
  const projectFor = (args) =>
    resolveEntity(projects(), { id: args.projectId, name: args.projectName }) || (!args.projectId && !args.projectName ? active().project : null);

  return [
    {
      name: "app.navigate",
      risk: "navigate",
      description:
        "Open a page in Quill. Paths: /home, /projects, /projects/<id> (add ?tab=sessions|requirements|documents), /customers, /customers/<id>, /customers/sessions, /customers/sessions/<id>, /templates, /templates/<id>/edit, /documents, /documents/<id>/edit, /generate. Only when the user asks to open or go somewhere.",
      parameters: {
        type: "object",
        properties: { path: { type: "string", description: "In-app path starting with /" } },
        required: ["path"],
      },
      label: (args) => `Opening ${describePath(args.path)}`,
      run: async ({ path }, ctx) => {
        const target = String(path || "").trim();
        if (!target.startsWith("/") || target.startsWith("//")) {
          return { ok: false, summary: "Invalid path", data: { error: "path must be an in-app path starting with /" } };
        }
        ctx?.markNavigated?.();
        latest.current.navigate(target);
        const pathOnly = target.split(/[?#]/)[0];
        const query = target.includes("?") ? target.slice(target.indexOf("?")).split("#")[0] : "";
        const editorKey = editorContextKey(pathOnly, query);
        if (!editorKey) {
          return { ok: true, summary: `Opened ${target}`, data: { ok: true, openedPath: target } };
        }
        // An editor route: wait (briefly) for that editor to mount, so the next round can read
        // and edit it (doc_read / doc_edit are offered only while an editor is open).
        const wantedId = editorKey.slice(editorKey.indexOf(":") + 1);
        const editorReady = () => {
          if (latest.current.pathname !== pathOnly) return false;
          const ref = latest.current.getEditorRef?.()?.current;
          if (!ref) return false;
          try {
            const meta = ref.getMeta?.();
            const openId = meta?.documentId || meta?.templateId;
            return !openId || String(openId) === wantedId;
          } catch {
            return true;
          }
        };
        const deadline = Date.now() + EDITOR_READY_TIMEOUT_MS;
        while (!editorReady() && Date.now() < deadline) {
          if (ctx?.signal?.aborted) throw new DOMException("Stopped", "AbortError");
          await new Promise((resolve) => setTimeout(resolve, EDITOR_READY_POLL_MS));
        }
        const ready = editorReady();
        return {
          ok: true,
          summary: `Opened ${target}`,
          data: {
            ok: true,
            openedPath: target,
            editorReady: ready,
            ...(ready ? {} : { note: "The editor is still loading; doc_read and doc_edit may not be available yet." }),
          },
        };
      },
    },
    {
      name: "app.open_settings",
      risk: "navigate",
      description: "Open the settings dialog.",
      parameters: { type: "object", properties: {} },
      label: () => "Opening settings",
      run: async () => {
        window.dispatchEvent(new Event("quillai:open-settings"));
        return { ok: true, summary: "Opened settings", data: { ok: true } };
      },
    },
    {
      name: "data.search",
      risk: "read",
      description:
        "Find customers, projects, templates, documents, sessions (meetings) or requirements by name/title. Returns ids. Use whenever you are not sure which entity the user means.",
      parameters: {
        type: "object",
        properties: {
          query: { type: "string", description: "Name or words to look for" },
          kinds: {
            type: "array",
            items: { type: "string", enum: ["customer", "project", "template", "document", "session", "requirement"] },
            description: "Limit to these kinds (default: all)",
          },
        },
        required: ["query"],
      },
      label: (args) => `Searching for "${oneLine(args.query, 40)}"`,
      run: async ({ query, kinds }) => {
        const wanted = new Set(Array.isArray(kinds) && kinds.length ? kinds : ["customer", "project", "template", "document", "session", "requirement"]);
        const results = [];
        const consider = (kind, item, name, extra = {}) => {
          const score = nameScore(name, query);
          if (score > 0) results.push({ kind, id: item.id, name, score, href: entityHref(kind, item), ...extra });
        };
        if (wanted.has("customer")) customers().forEach((c) => consider("customer", c, c.name));
        if (wanted.has("template")) templates().forEach((t) => consider("template", t, t.name));
        for (const p of projects()) {
          if (wanted.has("project")) consider("project", p, p.name);
          if (wanted.has("document")) (p.generatedDocuments || []).forEach((d) => consider("document", d, d.name, { projectId: p.id, projectName: p.name }));
          if (wanted.has("session")) (p.sessions || []).forEach((s) => consider("session", s, s.name, { projectId: p.id, projectName: p.name }));
          if (wanted.has("requirement")) (p.requirements || []).forEach((r) => consider("requirement", r, r.title, { projectId: p.id, projectName: p.name }));
        }
        results.sort((a, b) => b.score - a.score || String(a.name).localeCompare(String(b.name)));
        const top = results.slice(0, 20).map(({ score, ...rest }) => rest);
        return { ok: true, summary: `Found ${results.length} match${results.length === 1 ? "" : "es"}`, data: { query, results: top, total: results.length } };
      },
    },
    {
      name: "data.list_customers",
      risk: "read",
      description: "List customers with ids and project counts.",
      parameters: { type: "object", properties: {} },
      label: () => "Listing customers",
      run: async () => {
        const list = customers().map((c) => ({
          id: c.id,
          name: c.name,
          projects: projects().filter((p) => p.customer_id === c.id).length,
          href: entityHref("customer", c),
        }));
        return { ok: true, summary: `${list.length} customers`, data: { customers: list } };
      },
    },
    {
      name: "data.list_projects",
      risk: "read",
      description: "List projects with ids, customer and counts of requirements, sessions and documents. Optionally filter by customer.",
      parameters: {
        type: "object",
        properties: { customerId: { type: "string", description: "Only projects of this customer" } },
      },
      label: () => "Listing projects",
      run: async ({ customerId }) => {
        const list = projects()
          .filter((p) => !customerId || p.customer_id === customerId)
          .map((p) => ({
            id: p.id,
            name: p.name,
            customer: customers().find((c) => c.id === p.customer_id)?.name || p.customer?.name || null,
            requirements: (p.requirements || []).length,
            sessions: (p.sessions || []).length,
            documents: (p.generatedDocuments || []).length,
            href: entityHref("project", p),
          }));
        return { ok: true, summary: `${list.length} projects`, data: { projects: list } };
      },
    },
    {
      name: "data.list_templates",
      risk: "read",
      description: "List document templates with ids, type and description.",
      parameters: { type: "object", properties: {} },
      label: () => "Listing templates",
      run: async () => {
        const list = templates().map((t) => ({
          id: t.id,
          name: t.name,
          type: t.templateType || "document",
          description: oneLine(t.description, 200) || null,
          href: entityHref("template", t),
        }));
        return { ok: true, summary: `${list.length} templates`, data: { templates: list } };
      },
    },
    {
      name: "data.get_project",
      risk: "read",
      description:
        "Read a project: customer, description, requirement breakdown, recent sessions and documents (with ids). Defaults to the project on screen.",
      parameters: {
        type: "object",
        properties: {
          projectId: { type: "string", description: "Project id" },
          projectName: { type: "string", description: "Project name when the id is unknown" },
        },
      },
      label: (args) => {
        const project = projectFor(args);
        return project ? `Reading ${oneLine(project.name, 40)}` : "Reading the project";
      },
      run: async (args) => {
        const project = projectFor(args);
        if (!project) return notFound("project", args.projectId || args.projectName, projects());
        const reqs = project.requirements || [];
        const countBy = (key) =>
          reqs.reduce((acc, req) => {
            const value = req[key] || "Unspecified";
            acc[value] = (acc[value] || 0) + 1;
            return acc;
          }, {});
        const customer = customers().find((c) => c.id === project.customer_id) || project.customer || null;
        return {
          ok: true,
          summary: `Read ${project.name}`,
          data: {
            project: { id: project.id, name: project.name, description: project.description || null, href: entityHref("project", project) },
            customer: customer ? { id: customer.id, name: customer.name } : null,
            requirements: { total: reqs.length, byType: countBy("type"), byPriority: countBy("priority") },
            sessions: (project.sessions || []).slice(0, 15).map((s) => ({ id: s.id, name: s.name, date: s.createdAt ? String(s.createdAt).slice(0, 10) : null, source: s.source })),
            documents: (project.generatedDocuments || []).slice(0, 15).map((d) => ({ id: d.id, name: d.name, template: d.templateName || null, href: entityHref("document", d) })),
          },
        };
      },
    },
    {
      name: "data.get_requirements",
      risk: "read",
      description:
        "Read a project's requirements (ids, titles, type, priority, description). Filter by type, priority, session or words. Defaults to the project on screen.",
      parameters: {
        type: "object",
        properties: {
          projectId: { type: "string", description: "Project id" },
          type: { type: "string", description: "Requirement type, e.g. User Story" },
          priority: { type: "string", description: "Priority, e.g. High" },
          sessionId: { type: "string", description: "Only requirements extracted from this session" },
          query: { type: "string", description: "Words that must appear in the title or description" },
          limit: { type: "integer", description: "Max results (default 40, max 100)" },
        },
      },
      label: (args) => {
        const project = projectFor(args);
        return project ? `Reading requirements for ${oneLine(project.name, 36)}` : "Reading requirements";
      },
      run: async (args) => {
        const project = projectFor(args);
        if (!project) return notFound("project", args.projectId, projects());
        const q = String(args.query || "").toLowerCase().trim();
        const matches = sortRequirements(project.requirements).filter((req) => {
          if (args.type && String(req.type || "").toLowerCase() !== String(args.type).toLowerCase()) return false;
          if (args.priority && String(req.priority || "").toLowerCase() !== String(args.priority).toLowerCase()) return false;
          if (args.sessionId && req.sessionId !== args.sessionId) return false;
          if (q && !`${req.title} ${req.description}`.toLowerCase().includes(q)) return false;
          return true;
        });
        const limit = Math.min(Math.max(Number(args.limit) || 40, 1), 100);
        return {
          ok: true,
          summary: `${matches.length} requirement${matches.length === 1 ? "" : "s"} in ${project.name}`,
          data: {
            project: { id: project.id, name: project.name },
            total: matches.length,
            requirements: matches.slice(0, limit).map((req) => ({
              id: req.id,
              title: req.title,
              type: req.type || null,
              priority: req.priority || null,
              description: oneLine(req.description, 400),
              sessionId: req.sessionId || null,
            })),
          },
        };
      },
    },
    {
      name: "data.get_project_sessions",
      risk: "read",
      description: "Read a project's sessions (meetings) with their summaries and action items. Defaults to the project on screen.",
      parameters: {
        type: "object",
        properties: {
          projectId: { type: "string", description: "Project id" },
          projectName: { type: "string", description: "Project name when the id is unknown" },
        },
      },
      label: (args) => {
        const project = projectFor(args);
        return project ? `Reading meetings for ${oneLine(project.name, 36)}` : "Reading meetings";
      },
      run: async (args) => {
        const project = projectFor(args);
        if (!project) return notFound("project", args.projectId || args.projectName, projects());
        const sessions = (project.sessions || []).slice(0, 30);
        const insights = await loadSessionInsights(sessions.map((s) => s.id));
        return {
          ok: true,
          summary: `${sessions.length} meetings in ${project.name}`,
          data: {
            project: { id: project.id, name: project.name },
            sessions: sessions.map((s) => ({
              id: s.id,
              name: s.name,
              date: s.createdAt ? String(s.createdAt).slice(0, 10) : null,
              source: s.source,
              summary: oneLine(summaryToText(insights[s.id]?.summary || s.summary), 900) || null,
              actions: insights[s.id]?.actions || [],
              href: entityHref("session", s),
            })),
          },
        };
      },
    },
    {
      name: "data.get_session_detail",
      risk: "read",
      description: "Read one session (meeting): summary, action items, and a transcript excerpt. Defaults to the session on screen.",
      parameters: {
        type: "object",
        properties: {
          sessionId: { type: "string", description: "Session id" },
          includeTranscript: { type: "boolean", description: "Include a transcript excerpt (default true)" },
        },
      },
      label: (args) => {
        const id = args.sessionId || active().session?.id;
        const session = projects().flatMap((p) => p.sessions || []).find((s) => s.id === id);
        return session ? `Reading meeting "${oneLine(session.name, 36)}"` : "Reading the meeting";
      },
      run: async (args) => {
        const id = args.sessionId || active().session?.id;
        let session = null;
        let project = null;
        for (const p of projects()) {
          const found = (p.sessions || []).find((s) => s.id === id);
          if (found) {
            session = found;
            project = p;
            break;
          }
        }
        if (!session) return notFound("session", id, projects().flatMap((p) => p.sessions || []));
        const insights = await loadSessionInsights([session.id]);
        const result = {
          session: { id: session.id, name: session.name, date: session.createdAt ? String(session.createdAt).slice(0, 10) : null, source: session.source, href: entityHref("session", session) },
          project: { id: project.id, name: project.name },
          summary: summaryToText(insights[session.id]?.summary || session.summary),
          actions: insights[session.id]?.actions || [],
          requirementsFromSession: (project.requirements || []).filter((r) => r.sessionId === session.id).map((r) => ({ id: r.id, title: r.title })),
        };
        if (args.includeTranscript !== false && session.transcript) {
          const content = session.transcript?.content || (typeof session.transcript === "string" ? session.transcript : null);
          if (content) result.transcriptExcerpt = String(content).slice(0, 4000);
        }
        return { ok: true, summary: `Read ${session.name}`, data: result };
      },
    },
    {
      name: "data.get_customer_insights",
      risk: "read",
      description: "Everything about a customer: projects, sessions with summaries and action items. Use for questions about a customer's meetings, actions or outcomes.",
      parameters: {
        type: "object",
        properties: {
          customerId: { type: "string", description: "Customer id" },
          customerName: { type: "string", description: "Customer name when the id is unknown" },
        },
      },
      label: (args) => {
        const customer = resolveEntity(customers(), { id: args.customerId, name: args.customerName }) || active().customer;
        return customer ? `Reviewing ${oneLine(customer.name, 40)}` : "Reviewing the customer";
      },
      run: async (args) => {
        const customer = resolveEntity(customers(), { id: args.customerId, name: args.customerName }) || (!args.customerId && !args.customerName ? active().customer : null);
        if (!customer) return notFound("customer", args.customerId || args.customerName, customers());
        const own = projects().filter((p) => p.customer_id === customer.id);
        const insights = await loadSessionInsights(own.flatMap((p) => (p.sessions || []).slice(0, 20).map((s) => s.id)));
        return {
          ok: true,
          summary: `Reviewed ${customer.name}`,
          data: {
            customer: { id: customer.id, name: customer.name, description: customer.description || null, href: entityHref("customer", customer) },
            projects: own.map((p) => ({
              id: p.id,
              name: p.name,
              requirements: (p.requirements || []).length,
              sessions: (p.sessions || []).slice(0, 20).map((s) => ({
                id: s.id,
                name: s.name,
                date: s.createdAt ? String(s.createdAt).slice(0, 10) : null,
                summary: oneLine(summaryToText(insights[s.id]?.summary || s.summary), 600) || null,
                actions: insights[s.id]?.actions || [],
              })),
            })),
          },
        };
      },
    },
    {
      name: "data.get_template",
      risk: "read",
      description: "Read a template: description, type, section outline and number of AI fields.",
      parameters: {
        type: "object",
        properties: {
          templateId: { type: "string", description: "Template id" },
          templateName: { type: "string", description: "Template name when the id is unknown" },
        },
      },
      label: (args) => {
        const template = resolveEntity(templates(), { id: args.templateId, name: args.templateName });
        return template ? `Reading template "${oneLine(template.name, 36)}"` : "Reading the template";
      },
      run: async (args) => {
        const template = resolveEntity(templates(), { id: args.templateId, name: args.templateName }) || (!args.templateId && !args.templateName ? active().template : null);
        if (!template) return notFound("template", args.templateId || args.templateName, templates());
        const content = String(template.content || "");
        const headings = [...content.matchAll(/<h([1-4])[^>]*>([\s\S]*?)<\/h\1>/gi)].slice(0, 40).map((m) => `${"#".repeat(Number(m[1]))} ${htmlToText(m[2])}`);
        const fields = (content.match(/data-tpl-field=/g) || []).length;
        const legacyFields = (content.replace(/\{\{[^}]*\}\}/g, "").match(/\{[^{}]{3,}\}/g) || []).length;
        return {
          ok: true,
          summary: `Read ${template.name}`,
          data: {
            template: { id: template.id, name: template.name, type: template.templateType || "document", description: template.description || null, href: entityHref("template", template) },
            outline: headings,
            aiFields: fields + legacyFields,
            excerpt: htmlToText(content).slice(0, 1500),
          },
        };
      },
    },
    {
      name: "doc.read",
      risk: "read",
      description: "Read the open document or template in full detail: every block with its id and text, the selection and pending suggestions.",
      parameters: { type: "object", properties: {} },
      available: () => editorOpen(),
      label: () => "Reading the document",
      run: async () => {
        const snapshot = snapshotEditor(latest.current.getEditorRef, { fallbackMeta: editorFallbackMeta() });
        if (!snapshot) return { ok: false, summary: "No editor open", data: { error: "No document or template is open." } };
        return {
          ok: true,
          summary: `Read ${snapshot.blocks.length} blocks`,
          data: {
            meta: snapshot.meta,
            blocks: snapshot.blocks.slice(0, 200).map((block) => ({ id: block.id, type: block.type, level: block.level || undefined, locked: block.locked || undefined, text: oneLine(block.text, 600) })),
            selection: snapshot.selection ? { text: oneLine(snapshot.selection.text, 2000), blockIds: snapshot.selection.blockIds || [] } : null,
            pendingSuggestions: snapshot.pendingReview?.changes?.length || 0,
          },
        };
      },
    },
    {
      name: "doc.edit",
      risk: "write",
      description:
        "Draft, rewrite, extend, shorten, restructure or fix the OPEN document or template. Fills an empty document with a grounded first draft from the project's requirements and meeting notes. Changes appear as suggestions the user accepts or rejects in the editor.",
      parameters: {
        type: "object",
        properties: {
          instruction: {
            type: "string",
            description: "Complete, self-contained instruction including everything relevant from the conversation (tone, length, sections, facts to use).",
          },
          targetBlockIds: {
            type: "array",
            items: { type: "string" },
            description: "Ids of the blocks the user refers to (from the outline or the selection). Omit for whole-document changes.",
          },
          refine: { type: "boolean", description: "True when this adjusts the edit the user is currently reviewing." },
        },
        required: ["instruction"],
      },
      available: () => editorOpen(),
      label: () => "Editing the document",
      run: async (args, ctx) => {
        const scope = active();
        return runDocEdit({
          getEditorRef: latest.current.getEditorRef,
          instruction: String(args.instruction || "").trim(),
          targetBlockIds: Array.isArray(args.targetBlockIds) ? args.targetBlockIds : [],
          refine: Boolean(args.refine),
          project: scope.kind === "document" ? scope.project : null,
          fallbackMeta: editorFallbackMeta(),
          variant: ctx?.variant || 0,
          accountId: latest.current.activeAccount?.id,
          signal: ctx?.signal,
          onProgress: ctx?.onProgress,
        });
      },
    },
    {
      name: "doc.generate",
      risk: "write",
      approval: "form",
      description:
        "Generate a NEW document from a template and a project's requirements (runs in the background). Shows the user a prefilled form to confirm; pass whatever you know.",
      parameters: {
        type: "object",
        properties: {
          projectId: { type: "string", description: "Project id" },
          templateId: { type: "string", description: "Template id" },
          documentName: { type: "string", description: "Name for the new document" },
          tone: { type: "string", enum: AI_TONES.map((t) => t.title), description: "Writing tone" },
        },
      },
      label: () => "Preparing the document form",
      run: async (args) => {
        const project = resolveEntity(projects(), { id: args.projectId }) || active().project || (projects().length === 1 ? projects()[0] : null);
        const template = resolveEntity(templates(), { id: args.templateId }) || active().template || (templates().length === 1 ? templates()[0] : null);
        const tone = AI_TONES.some((t) => t.title === args.tone) ? args.tone : DEFAULT_TONE;
        const documentName = String(args.documentName || "").trim() || (project && template ? `${project.name} - ${template.name}` : "");
        const values = { projectId: project?.id || "", templateId: template?.id || "", documentName, tone };
        const missing = Object.entries({ projectId: "project", templateId: "template", documentName: "document name" })
          .filter(([key]) => !values[key])
          .map(([, label]) => label);
        return {
          ok: true,
          summary: "Prepared the document form",
          data: {
            status: "awaiting_user_confirmation",
            prefilled: { project: project?.name || null, template: template?.name || null, documentName: documentName || null, tone },
            missing,
            note: "A form card is shown to the user; nothing is generated until they confirm it.",
          },
          card: { kind: "doc_generate", status: "pending", values, missing },
        };
      },
      commit: async (values) => {
        const project = projects().find((p) => p.id === values.projectId);
        const template = templates().find((t) => t.id === values.templateId);
        if (!project || !template || !String(values.documentName || "").trim()) {
          throw new Error("Choose a project, a template and a document name.");
        }
        const usage = await latest.current.refreshUsage?.();
        if (usage && hasReachedLimit(usage, "documents")) {
          throw new Error("You've reached your monthly document limit. Upgrade your plan to generate more documents.");
        }
        const tone = AI_TONES.some((t) => t.title === values.tone) ? values.tone : DEFAULT_TONE;
        const documentName = String(values.documentName).trim();
        const request = await latest.current.createRequest({
          type: "document",
          title: documentName,
          projectId: project.id,
          templateId: template.id,
          priority: 0,
          data: { tone, contentSource: "requirements", existingDocumentId: null },
        });
        await edgeFunctionService.generateDocument({
          requestId: request.id,
          projectId: project.id,
          templateId: template.id,
          documentName,
          tone,
          contentSource: "requirements",
          existingDocumentId: null,
        });
        latest.current.refreshUsage?.();
        notifyDataChanged("document");
        return {
          ok: true,
          summary: `Queued "${documentName}"`,
          outcome: `Generation of "${documentName}" from ${template.name} for ${project.name} is queued. Progress shows in the notifications.`,
          entity: { kind: "project", id: project.id, name: project.name, href: `${entityHref("project", project)}?tab=documents`, linkLabel: "Open project documents" },
        };
      },
    },
    {
      name: "requirements.stage",
      risk: "write",
      approval: "confirm",
      description:
        "Stage additions, updates or removals of a project's requirements for the user to approve on a card (nothing is saved until they press Approve). Every add or update needs sourceRef: a requirement (R-xxxxxx) or meeting (S-xxxxxx) ref from a tool result, or 'user' with sourceQuote when the user said it.",
      parameters: {
        type: "object",
        properties: {
          projectId: { type: "string", description: "Project id (defaults to the project on screen)" },
          changes: {
            type: "array",
            items: {
              type: "object",
              properties: {
                action: { type: "string", enum: ["add", "update", "delete"] },
                requirementId: { type: "string", description: "update/delete: requirement id or R- ref" },
                title: { type: "string" },
                description: { type: "string" },
                type: { type: "string" },
                priority: { type: "string", enum: ["Critical", "High", "Medium", "Low"] },
                sessionId: { type: "string", description: "Meeting id or S- ref" },
                acceptanceCriteria: { type: "array", items: { type: "string" } },
                sourceRef: { type: "string" },
                sourceTimestamp: { type: "string" },
                sourceQuote: { type: "string" },
              },
              required: ["action"],
            },
          },
          summary: { type: "string" },
        },
        required: ["changes"],
      },
      label: () => "Staging requirement changes",
      run: async (args) => {
        const project = projectFor(args);
        if (!project) return notFound("project", args.projectId, projects());
        const staged = stageRequirementChanges({ project, changes: args.changes, summary: args.summary });
        if (!staged.ok) {
          return { ok: false, summary: "Changes refused, correcting", data: { error: "Nothing was staged. Fix these and send the complete corrected list.", problems: staged.errors } };
        }
        return {
          ok: true,
          summary: `Staged ${staged.card.changes.length} requirement change${staged.card.changes.length === 1 ? "" : "s"}`,
          data: { status: "awaiting_user_approval", counts: staged.counts, note: "A card shows the changes; nothing is saved until the user presses Approve all." },
          card: staged.card,
        };
      },
      commit: async (values, { card } = {}) => {
        if (!card?.projectId) throw new Error("This change set is no longer available.");
        const result = await commitRequirementChanges({ card, values, actions: latest.current.actions });
        notifyDataChanged("requirement");
        const project = projects().find((p) => p.id === card.projectId);
        return {
          ...result,
          entity: project ? { kind: "project", id: project.id, name: project.name, href: `${entityHref("project", project)}?tab=requirements`, linkLabel: "Open requirements" } : null,
        };
      },
    },
    {
      name: "data.create_customer",
      risk: "write",
      approval: "confirm",
      description: "Create a customer. The user confirms on a card before anything is saved.",
      parameters: {
        type: "object",
        properties: {
          name: { type: "string", description: "Customer name" },
          description: { type: "string", description: "Short description" },
        },
        required: ["name"],
      },
      available: () => Boolean(latest.current.activeAccount),
      label: () => "Preparing a new customer",
      run: async (args) => {
        const name = String(args.name || "").trim();
        const existing = resolveEntity(customers(), { name });
        if (existing && nameScore(existing.name, name) === 100) {
          return { ok: false, summary: "Customer exists", data: { error: `A customer named "${existing.name}" already exists.`, existing: { id: existing.id, name: existing.name } } };
        }
        const values = { name, description: String(args.description || "").trim() };
        return {
          ok: true,
          summary: "Prepared a new customer",
          data: { status: "awaiting_user_confirmation", customer: values, note: "Nothing is created until the user confirms the card." },
          card: { kind: "confirm", status: "pending", title: "Create customer", values, fields: [{ key: "name", label: "Name", required: true }, { key: "description", label: "Description", multiline: true }] },
        };
      },
      commit: async (values) => {
        const name = String(values.name || "").trim();
        if (!name) throw new Error("A customer name is required.");
        const result = await latest.current.actions.createCustomer({ name, description: String(values.description || "").trim() });
        if (!result?.success) throw new Error(result?.error || "Could not create the customer.");
        notifyDataChanged("customer");
        return {
          ok: true,
          summary: `Created customer ${result.data.name}`,
          outcome: `Created customer "${result.data.name}".`,
          entity: { kind: "customer", id: result.data.id, name: result.data.name, href: entityHref("customer", result.data) },
        };
      },
      undo: async (card) => {
        const id = card?.entity?.id;
        if (!id) throw new Error("This customer can no longer be undone.");
        const { count, error: countError } = await supabase.from("projects").select("id", { count: "exact", head: true }).eq("customer_id", id);
        if (countError) throw countError;
        if (count > 0) throw new Error("This customer already has projects. Undo or remove those first.");
        const { data: removed, error } = await supabase.from("customers").delete().eq("id", id).select("id");
        if (error) throw error;
        if (!removed?.length) throw new Error("This customer could not be removed (it may already be gone, or you lack permission).");
        latest.current.actions.loadCustomers?.();
        notifyDataChanged("customer");
        return { ok: true, outcome: `Removed customer "${card.entity.name}".` };
      },
    },
    {
      name: "data.create_project",
      risk: "write",
      approval: "confirm",
      description: "Create a project under a customer. The user confirms on a card before anything is saved.",
      parameters: {
        type: "object",
        properties: {
          name: { type: "string", description: "Project name" },
          customerId: { type: "string", description: "Customer id (from the directory or data_search)" },
          description: { type: "string", description: "Short description" },
        },
        required: ["name", "customerId"],
      },
      available: () => Boolean(latest.current.activeAccount),
      label: () => "Preparing a new project",
      run: async (args) => {
        const customer = resolveEntity(customers(), { id: args.customerId });
        if (!customer) return notFound("customer", args.customerId, customers());
        const values = { name: String(args.name || "").trim(), customerId: customer.id, description: String(args.description || "").trim() };
        return {
          ok: true,
          summary: "Prepared a new project",
          data: { status: "awaiting_user_confirmation", project: { ...values, customer: customer.name }, note: "Nothing is created until the user confirms the card." },
          card: {
            kind: "confirm",
            status: "pending",
            title: "Create project",
            subtitle: `Customer: ${customer.name}`,
            values,
            fields: [{ key: "name", label: "Name", required: true }, { key: "description", label: "Description", multiline: true }],
          },
        };
      },
      commit: async (values) => {
        const name = String(values.name || "").trim();
        if (!name || !values.customerId) throw new Error("A project name and customer are required.");
        const result = await latest.current.actions.createProject({ name, description: String(values.description || "").trim(), customer_id: values.customerId });
        if (!result?.success) throw new Error(result?.error || "Could not create the project.");
        // Reload so the new project carries its customer join like every other project.
        latest.current.actions.loadProjects?.();
        notifyDataChanged("project");
        return {
          ok: true,
          summary: `Created project ${result.data.name}`,
          outcome: `Created project "${result.data.name}".`,
          entity: { kind: "project", id: result.data.id, name: result.data.name, href: entityHref("project", result.data) },
        };
      },
      undo: async (card) => {
        const id = card?.entity?.id;
        if (!id) throw new Error("This project can no longer be undone.");
        // Anything that hangs off the project (several cascade on delete) means it is in use.
        const inUse = await Promise.all(
          PROJECT_CHILD_TABLES.map((table) =>
            supabase.from(table).select("project_id", { count: "exact", head: true }).eq("project_id", id)
          )
        );
        const failed = inUse.find((r) => r.error);
        if (failed) throw failed.error;
        if (inUse.some((r) => (r.count || 0) > 0)) {
          throw new Error("This project already has meetings, requirements, documents, files or shared members, so it was kept.");
        }
        const { data: removed, error } = await supabase.from("projects").delete().eq("id", id).select("id");
        if (error) throw error;
        if (!removed?.length) throw new Error("This project could not be removed (it may already be gone, or you lack permission).");
        latest.current.actions.loadProjects?.();
        notifyDataChanged("project");
        return { ok: true, outcome: `Removed project "${card.entity.name}".` };
      },
    },
    {
      name: "data.link_sessions",
      risk: "write",
      approval: "confirm",
      description: "Link meetings to projects. The user confirms on a card unless they asked for it directly.",
      parameters: {
        type: "object",
        properties: {
          links: { type: "array", items: { type: "object", properties: { sessionId: { type: "string" }, projectId: { type: "string" } } } },
        },
        required: ["links"],
      },
      // The server prepares link cards; the browser only commits and undoes them (found by name).
      available: () => false,
      label: () => "Linking meetings",
      run: async () => ({ ok: false, summary: "Unavailable", data: { error: "Linking meetings is prepared by the server." } }),
      commit: async (values) => {
        const links = Array.isArray(values?.links) ? values.links : [];
        if (!links.length) throw new Error("There is nothing to link.");
        const results = [];
        for (const link of links) {
          const { data: before, error: readError } = await supabase
            .from("sessions")
            .select("id, project_id, source, extraction_type")
            .eq("id", link.sessionId)
            .maybeSingle();
          if (readError) throw readError;
          if (!before) throw new Error("One of these meetings is no longer available.");
          const patch = { project_id: link.projectId };
          const setExtraction = before.source === "recall" && !before.extraction_type;
          if (setExtraction) {
            patch.extraction_type = "User Story";
            patch.extraction_config = { artifactFormat: "User Story", artifactFormatOther: "", excludedDimensions: [] };
          }
          const { error } = await supabase.from("sessions").update(patch).eq("id", link.sessionId);
          if (error) {
            if (!results.length) throw error;
            // Keep what was saved (and undoable); say what stopped.
            notifyDataChanged("session");
            return {
              ok: true,
              summary: `Linked ${results.length} of ${links.length} meetings`,
              outcome: `Linked ${results.length} of ${links.length} meetings; the rest failed (${error.message}).`,
              results,
            };
          }
          results.push({ sessionId: link.sessionId, previousProjectId: before.project_id || null, projectId: link.projectId, setExtraction });
        }
        notifyDataChanged("session");
        return { ok: true, summary: `Linked ${results.length} meetings`, outcome: `Linked ${results.length} meeting${results.length === 1 ? "" : "s"}.`, results };
      },
      undo: async (card) => {
        const results = Array.isArray(card?.results) ? card.results : [];
        if (!results.length) throw new Error("These links can no longer be undone.");
        let restored = 0;
        let skipped = 0;
        for (const r of results) {
          // Only undo a link that is still in place: a meeting moved since then is left alone.
          // Also take back the default extraction settings the link added, if it added them.
          const revert = r.setExtraction
            ? { project_id: r.previousProjectId, extraction_type: null, extraction_config: null }
            : { project_id: r.previousProjectId };
          let { data, error } = await supabase
            .from("sessions")
            .update(revert)
            .eq("id", r.sessionId)
            .eq("project_id", r.projectId)
            .select("id");
          if (error && r.setExtraction && /extraction_config/i.test(String(error.message || ""))) {
            const { extraction_config: _omit, ...rest } = revert;
            ({ data, error } = await supabase
              .from("sessions")
              .update(rest)
              .eq("id", r.sessionId)
              .eq("project_id", r.projectId)
              .select("id"));
          }
          if (error) throw error;
          if (data?.length) restored += 1;
          else skipped += 1;
        }
        notifyDataChanged("session");
        const tail = skipped ? ` ${skipped} had been moved since and were left as they are.` : "";
        return { ok: true, outcome: `Undid ${restored} meeting link${restored === 1 ? "" : "s"}.${tail}` };
      },
    },
  ];
};

/**
 * Inky's tool registry for the current app state.
 * @param {{contextData: object}} params - Route-aware entity data from the app shell.
 * @returns {Array<object>} Stable tool registry (reads fresh state at call time).
 */
export function useAgentTools({ contextData }) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { actions } = useApp();
  const { state: authState } = useAuth();
  const { createRequest } = useAIRequests();
  const { getEditorRef } = useActiveEditor();
  const { refreshUsage } = useUsage();

  const latest = useRef({});
  latest.current = {
    navigate,
    pathname,
    search,
    actions,
    contextData,
    activeAccount: authState.activeAccount,
    createRequest,
    getEditorRef,
    refreshUsage,
  };

  return useMemo(() => buildTools(latest), []);
}

/**
 * Read-only check used by tests and the panel: does this tool need a user confirmation card?
 * @param {object} tool - Registry tool.
 * @returns {boolean} True for approval-gated tools.
 */
export const requiresApproval = (tool) => Boolean(tool?.approval);

// Exposed for unit tests.
export const __private = { nameScore, resolveEntity };
